import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  CreateExternalCryptoAddressInput,
  CryptoAddressHostType,
  CryptoTransfersVASPQuery$data,
  OwnerType,
  UpdateExternalCryptoAddressInput,
} from '@bts-web/data-layer/server';
import { getMemoType } from '../../../utils/getMemoType';
import { validateCryptoAddress } from '../../SendScreen/actions/validateCryptoAddress.action';
import { OtherWalletId } from '../SelectWalletHostScreen/SelectWalletHostScreen';
import { TTransfersNextStepStateWithActions } from '../../../utils/useTransfersNextStepState.client';
import { TAddNewWalletTranslations } from '../../../utils/getCryptoTransferTranslations';
import { WalletOwnerType } from '../../../types';

export interface WalletFormInputsProps {
  networkData?: TTransfersNextStepStateWithActions['selectedNetworkData'];
  assetData: TTransfersNextStepStateWithActions['selectedAssetData'];
  walletHosts: CryptoTransfersVASPQuery$data['cryptoTransfersVASP'];
  translations: TAddNewWalletTranslations;
  onCreate?: (addressInput: CreateExternalCryptoAddressInput) => void;
  onUpdate?: (address: Partial<UpdateExternalCryptoAddressInput>) => void;
  defaultAddressInput?: Partial<CreateExternalCryptoAddressInput> | null;
  isLegalEntity?: boolean;
}

const WALLET_NAME_REGEX = /[0-9a-zA-Z\-.']+\s[0-9a-zA-Z\-.']+/;

const WALLET_NAME_INPUT_MAX_LENGTH = 128;

const useYupValidationResolver = (validationSchema: yup.Schema) =>
  useCallback(
    async (data: any) => {
      try {
        const values = await validationSchema.validate(data, {
          abortEarly: false,
        });

        return {
          values,
          errors: {},
        };
      } catch (errors: unknown) {
        if (errors instanceof yup.ValidationError) {
          return {
            values: {},
            errors: errors.inner.reduce(
              (allErrors, currentError) => ({
                ...allErrors,
                [currentError.path as string]: {
                  type: currentError.type ?? 'validation',
                  message: currentError.message,
                },
              }),
              {},
            ),
          };
        }

        throw errors;
      }
    },
    [validationSchema],
  );

export const useWalletFormInput = ({
  translations,
  assetData,
  networkData,
  walletHosts,
  onCreate,
  onUpdate,
  defaultAddressInput,
  isLegalEntity = false,
}: WalletFormInputsProps) => {
  const defaultWalletHost = useMemo(() => {
    if (!defaultAddressInput?.hostName) return null;

    if (defaultAddressInput?.hostType === 'UNKNOWN_VASP') {
      return {
        id: OtherWalletId.OTHER,
        name: translations.otherHost,
        category: 'OTHER',
      };
    }

    if (defaultAddressInput?.hostType === 'SELF_HOSTED') {
      return {
        id: OtherWalletId.SELF_HOSTED,
        name: translations.selfHostedWallet,
        category: 'OTHER',
      };
    }

    return walletHosts?.edges?.find(
      (host) =>
        host?.node?.name === defaultAddressInput.hostName ||
        host?.node?.id === defaultAddressInput.hostName,
    )?.node;
  }, [defaultAddressInput, walletHosts]);

  const walletFormSchema = useMemo(
    () =>
      yup.object().shape({
        recipientName: isLegalEntity
          ? yup
              .string()
              .default(defaultAddressInput?.ownerLegalName)
              .required(translations?.errorBusinessNameRequired)
              .max(
                WALLET_NAME_INPUT_MAX_LENGTH,
                translations?.errorBusinessNameTooLong,
              )
          : yup
              .string()
              .default(defaultAddressInput?.ownerLegalName)
              .when('isMyWallet', {
                is: false,
                then: (schema) =>
                  schema
                    .required(translations?.errorInvalidFormatRecipientName)
                    .max(
                      WALLET_NAME_INPUT_MAX_LENGTH,
                      translations?.errorInvalidFormatRecipientName,
                    )
                    .matches(
                      WALLET_NAME_REGEX,
                      translations?.errorInvalidFormatRecipientName,
                    ),
              }),
        leiNumber: yup
          .string()
          .default(
            isLegalEntity
              ? (defaultAddressInput?.ownerIdentifier ?? undefined)
              : undefined,
          )
          .when([], {
            is: () => isLegalEntity,
            then: (schema) =>
              schema
                .required(translations?.errorLeiNumberRequired)
                .length(20, translations?.errorLeiNumberInvalidLength)
                .matches(/^[A-Z0-9]+$/, translations?.errorInvalidLeiNumber),
          }),
        walletAddress: yup
          .string()
          .default(defaultAddressInput?.address)
          .required(translations.errorCheckWalletAddress),
        isMyWallet: yup
          .boolean()
          .default(defaultAddressInput?.userOwned || false),
        walletHost: yup.object().default(defaultWalletHost).required(),
        memo: yup.string(),
        otherHostName: yup.string().when('walletHost', {
          is: (value: any) => value?.id === OtherWalletId.OTHER,
          then: (schema) => schema.required(),
        }),
        isAgreed: yup.boolean().default(false),
      }),
    [translations, defaultAddressInput, defaultWalletHost, isLegalEntity],
  );

  const resolver = useYupValidationResolver(walletFormSchema);

  const {
    register,
    formState: { errors, isSubmitting, isValid: isFormValid },
    handleSubmit,
    control,
    watch,
    resetField,
    setError,
  } = useForm({
    resolver,
    defaultValues: walletFormSchema.getDefault(),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmitWalletForm = handleSubmit(async (data) => {
    let hostType: CryptoAddressHostType = 'KNOWN_VASP';

    if (data?.walletHost?.id === OtherWalletId.SELF_HOSTED) {
      hostType = 'SELF_HOSTED';
    }

    if (data?.walletHost?.id === OtherWalletId.OTHER) {
      hostType = 'UNKNOWN_VASP';
    }

    const commonFormData = {
      ...(hostType === 'KNOWN_VASP' ? { hostId: data?.walletHost?.id } : {}),
      ...(hostType === 'UNKNOWN_VASP' ? { hostName: data?.otherHostName } : {}),
      hostType,
      ownerLegalName: data?.recipientName,
      ownerType: (isLegalEntity
        ? WalletOwnerType.LegalEntity
        : WalletOwnerType.NaturalPerson) as OwnerType,
      ...(isLegalEntity ? { ownerIdentifier: data?.leiNumber } : {}),
      userOwned: isLegalEntity ? false : data?.isMyWallet,
    };

    if (onUpdate) {
      await onUpdate(commonFormData);
    } else if (onCreate) {
      const { errors: addressValidationErrors, data: validationData } =
        await validateCryptoAddress({
          coinNetworkId: networkData?.coinNetworkId as string,
          toAddress: data.walletAddress as string,
          ...(networkData?.destinationTag && data.memo
            ? {
                destinationTag: data.memo,
                destinationTagType: getMemoType(
                  data.memo,
                  assetData?.symbol as string,
                ),
              }
            : {}),
        });

      const validationResponseData = validationData?.validateCryptoAddress;

      if (
        addressValidationErrors ||
        !validationResponseData?.isAddressValid ||
        !validationResponseData?.isValid
      ) {
        if (validationResponseData?.isDestinationTagValid === false) {
          setError('memo', {
            type: 'manual',
            message: translations.errorCheckMemo,
          });
        } else {
          setError('walletAddress', {
            type: 'manual',
            message: translations.errorCheckWalletAddress,
          });
        }

        return;
      }

      await onCreate({
        ...commonFormData,
        address: data?.walletAddress?.trim() as string,
        networkId: networkData?.coinNetworkId as string,
        ...(networkData?.destinationTag && data?.memo
          ? {
              destinationTag: data?.memo as string,
            }
          : {}),
      });
    }
  });

  const isMyWallet = watch('isMyWallet', defaultAddressInput?.userOwned);

  const walletHost = watch('walletHost', defaultWalletHost);

  const isAgreed = watch('isAgreed', false);

  const isSubmitDisabled = useMemo(() => {
    return !isAgreed || !isFormValid;
  }, [isAgreed, isFormValid]);

  const recipientNameInputProps = register('recipientName', {
    onChange: () => {
      resetField('isAgreed');
    },
  });

  const leiNumberInputProps = register('leiNumber', {
    onChange: () => {
      resetField('isAgreed');
    },
  });

  const isMyWalletInputProps = {
    onChange: () => {
      resetField('isAgreed');
    },
  };

  const walletAddressInputProps = register('walletAddress', {
    onChange: () => {
      resetField('isAgreed');
    },
  });

  const memoInputProps = register('memo', {
    onChange: () => {
      resetField('isAgreed');
    },
  });

  const otherHostNameInputProps = register('otherHostName', {
    onChange: () => {
      resetField('isAgreed');
    },
  });

  const walletHostInputProps = {
    onChange: () => {
      resetField('isAgreed');
    },
  };

  return {
    errors,
    isSubmitting,
    control,
    isMyWallet,
    walletHost,
    defaultWalletHost,
    isSubmitDisabled,
    onSubmitWalletForm,
    resetField,
    recipientNameInputProps,
    leiNumberInputProps,
    isMyWalletInputProps,
    walletAddressInputProps,
    memoInputProps,
    otherHostNameInputProps,
    walletHostInputProps,
  };
};
